import { message } from "antd";
import axios from "axios";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import {
  cancelEHRTransfer,
  getActiveTransfer,
  selectTransferAppointment,
  startEHRTransfer,
  submitTransferMFACode,
} from "../../service/notes.service";
import type {
  AppointmentOption,
  EHRCredentialCheckResponse,
  EHRTransferStatus,
  StartEHRTransferResponse,
  TransferJob,
} from "../../types/index.type";
import { eventEmitter } from "../../utils/eventEmitter";

// Polling cadences. Active transfer keeps the existing 5s rhythm; idle backs
// off so the app doesn't hammer the API just sitting on the dashboard.
const ACTIVE_POLL_INTERVAL_MS = 5000;
const IDLE_POLL_INTERVAL_MS = 10000;
const ACTIVE_STATUSES: EHRTransferStatus[] = [
  "pending",
  "logging_in",
  "awaiting_mfa",
  "patient_lookup",
  "awaiting_appointment",
  "awaiting_note_type",
  "template_mapping",
  "writing_note",
];
const TERMINAL_STATUSES: EHRTransferStatus[] = ["completed", "failed", "cancelled"];
// Statuses that block on the user. At most one of these is surfaced as the
// "primary" job driving the appointment/MFA modals at a time.
const AWAITING_INPUT_STATUSES: EHRTransferStatus[] = ["awaiting_mfa", "awaiting_appointment", "awaiting_note_type"];
const CHROME_EXTENSION_URL =
  "https://chromewebstore.google.com/detail/khnjmojbacjmfbplefmcinlmlbifhjnj?utm_source=item-share-cb";

// With several transfers in flight, exactly one drives the shared modals. Pick
// the one waiting on the user first (so pickers surface promptly), otherwise
// the most-recent active job, otherwise the most-recent (terminal) job for the
// success/error flash. Server returns jobs newest-first.
const pickPrimaryJob = (jobs: TransferJob[]): TransferJob | null => {
  if (!jobs.length) return null;
  return (
    jobs.find((j) => AWAITING_INPUT_STATUSES.includes(j.status)) ??
    jobs.find((j) => ACTIVE_STATUSES.includes(j.status)) ??
    jobs[0]
  );
};

const getUserFriendlyError = (error?: string): string => {
  if (!error) return "Transfer failed. Please try again or contact support.";
  // The client field is labelled "MRN/Patient ID" (some EHRs use a Patient ID, not
  // an MRN); reflect that in any backend-originated message we surface verbatim
  // (e.g. "Please add MRN and DOB…", "MRN … not found").
  return error.replace(/\bMRN\b/g, "MRN/Patient ID");
};

interface TransferContextValue {
  isLoading: boolean;
  credentialStatus: EHRCredentialCheckResponse | null;
  // The "primary" job — the one currently driving the shared appointment/MFA
  // modals (see pickPrimaryJob). Kept for the modal/status plumbing.
  activeJob: TransferJob | null;
  // Every in-flight (+ recently-finished) transfer. The banner renders all of
  // these; each note's button reads its own entry via getJobForAudio.
  activeJobs: TransferJob[];
  currentStatus: EHRTransferStatus;
  jobId: string | null;
  appointmentOptions: AppointmentOption[];
  showAppointmentModal: boolean;
  isSelectingAppointment: boolean;
  isNoteTypeSelection: boolean;
  showMFAModal: boolean;
  isSubmittingMFA: boolean;
  mfaOptions: Array<{ id: string; label: string }>;
  startTransfer: (audioId: number) => Promise<void>;
  selectAppointment: (appointmentId: string) => Promise<void>;
  cancelAppointment: () => void;
  submitMFACode: (code: string, method?: string) => Promise<void>;
  cancelMFA: () => void;
  // Cancel a specific in-flight transfer (banner cancel button).
  cancelTransfer: (jobId: string) => Promise<void>;
  // The most relevant live job for a given note, or null.
  getJobForAudio: (audioId: number) => TransferJob | null;
}

const TransferContext = createContext<TransferContextValue | null>(null);

export const useTransfer = () => {
  const context = useContext(TransferContext);
  if (!context) {
    throw new Error("useTransfer must be used within TransferProvider");
  }
  return context;
};

export const useOptionalTransfer = () => useContext(TransferContext);

interface TransferProviderProps {
  children: React.ReactNode;
}

export const TransferProvider: React.FC<TransferProviderProps> = ({ children }) => {
  const { userInfo } = useAppSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(false);
  const [activeJob, setActiveJob] = useState<TransferJob | null>(null);
  const [activeJobs, setActiveJobs] = useState<TransferJob[]>([]);
  const [currentStatus, setCurrentStatus] = useState<EHRTransferStatus>("not_started");
  const [jobId, setJobId] = useState<string | null>(null);
  const [appointmentOptions, setAppointmentOptions] = useState<AppointmentOption[]>([]);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [isSelectingAppointment, setIsSelectingAppointment] = useState(false);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [isSubmittingMFA, setIsSubmittingMFA] = useState(false);
  const [mfaOptions, setMfaOptions] = useState<Array<{ id: string; label: string }>>([]);

  // Derive credentialStatus from Redux. useMemo so it's a stable ref —
  // otherwise startTransfer's deps churn on every render.
  const credentialStatus = useMemo<EHRCredentialCheckResponse>(
    () => ({ has_credential: userInfo.enable_ehr_transfer ?? false }),
    [userInfo.enable_ehr_transfer],
  );

  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Latest poll() fn, exposed so imperative callers (startTransfer) can force
  // an immediate refresh instead of waiting out the idle poll interval.
  const pollRef = useRef<() => void>(() => {});
  // Modal-open dedupe: prevents reopening the same picker while paused.
  const lastModalKeyRef = useRef<string | null>(null);
  // MFA submission lock: don't re-show the modal after the user has submitted.
  const mfaSubmittedForJobRef = useRef<string | null>(null);
  // Last terminal status we already broadcast as a `ehrTransferCompleted` /
  // `ehrTransferFailed` event, keyed by `${jobId}:${status}`. Prevents
  // duplicate broadcasts when the recent-terminal window keeps the same job
  // visible across multiple polls.
  const lastTerminalKeyRef = useRef<string | null>(null);
  // Mirror of currentStatus + showMFAModal so the polling effect can read
  // them without re-subscribing whenever they change. Without this, every
  // status transition tears down and restarts the polling timer, which can
  // burst poll the API and is brittle around the visibilitychange listener.
  const currentStatusRef = useRef<EHRTransferStatus>("not_started");
  const showMFAModalRef = useRef<boolean>(false);
  // Mirror of the primary jobId so stable callbacks (cancelTransfer) can read
  // it without re-creating on every status change.
  const jobIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentStatusRef.current = currentStatus;
  }, [currentStatus]);
  useEffect(() => {
    showMFAModalRef.current = showMFAModal;
  }, [showMFAModal]);
  useEffect(() => {
    jobIdRef.current = jobId;
  }, [jobId]);

  const handleActiveUpdate = useCallback((job: TransferJob | null) => {
    setActiveJob(job);

    if (!job) {
      // No active or recently-finished job — reset transient picker state.
      setJobId(null);
      setCurrentStatus("not_started");
      setShowAppointmentModal(false);
      setShowMFAModal(false);
      return;
    }

    setJobId(job.id);
    setCurrentStatus(job.status);

    // MFA: open modal on transition into awaiting_mfa; respect submit lock.
    if (job.status === "awaiting_mfa" && job.requires_mfa_code && mfaSubmittedForJobRef.current !== job.id) {
      if (!showMFAModalRef.current) {
        setMfaOptions(job.mfa_options || []);
        setShowMFAModal(true);
      }
    }

    // Appointment / note-type pickers: status itself is the discriminator.
    const isAwaitingSelection =
      (job.status === "awaiting_appointment" && job.requires_appointment_selection) ||
      (job.status === "awaiting_note_type" && job.requires_note_type_selection);
    if (isAwaitingSelection && job.appointment_options && job.appointment_options.length > 0) {
      const modalKey = `${job.id}:${job.status}`;
      if (modalKey !== lastModalKeyRef.current) {
        setAppointmentOptions(job.appointment_options);
        setShowAppointmentModal(true);
        lastModalKeyRef.current = modalKey;
      }
    }

    // Terminal events: emit once per (job, status) pair so listeners can
    // react without duplicate fires across the recent-terminal poll window.
    if (TERMINAL_STATUSES.includes(job.status)) {
      const terminalKey = `${job.id}:${job.status}`;
      if (lastTerminalKeyRef.current !== terminalKey) {
        lastTerminalKeyRef.current = terminalKey;
        if (job.status === "completed") {
          eventEmitter.emit("ehrTransferCompleted", { audioId: job.audio_file_id ?? undefined });
        } else if (job.status === "failed") {
          eventEmitter.emit("ehrTransferFailed", { audioId: job.audio_file_id ?? undefined });
        }
      }
    }
  }, []);

  // Poll the global active-transfer endpoint. Mounted once for the lifetime
  // of the provider; cadence reads from currentStatusRef so status changes
  // don't tear down and restart the timer.
  //
  // Skip entirely when the user doesn't have the EHR transfer feature.
  // `/templates` is rendered inside `AuthLayout avoidTokenValidation`, so
  // unauthenticated visitors would otherwise be 401-spammed every 5–30s.
  // `enable_ehr_transfer` is false until userInfo loads after a successful
  // session, so this gate also covers the not-logged-in case.
  const ehrTransferEnabled = Boolean(userInfo?.enable_ehr_transfer);
  useEffect(() => {
    if (!ehrTransferEnabled) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await getActiveTransfer();
        if (cancelled) return;
        // Prefer the multi-job list; fall back to the legacy single `job`.
        const jobs: TransferJob[] = res.data?.jobs ?? (res.data?.job ? [res.data.job] : []);
        setActiveJobs(jobs);
        // One primary job drives the shared modals + status; the rest render
        // in the banner. This keeps the single-transfer flow byte-identical.
        handleActiveUpdate(pickPrimaryJob(jobs));
      } catch (error) {
        // 401 means the session expired or the user logged out mid-poll. No
        // point retrying — the axios interceptor handles the redirect, and
        // looping here just floods Sentry.
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          return;
        }
        console.error("Error polling active transfer:", error);
      }

      if (cancelled) return;

      const isTabHidden = typeof document !== "undefined" && document.visibilityState === "hidden";
      if (isTabHidden) {
        // Don't schedule another poll while hidden; visibilitychange below
        // will kick us back in when the tab becomes active.
        return;
      }

      const next = ACTIVE_STATUSES.includes(currentStatusRef.current) ? ACTIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS;
      pollingTimeoutRef.current = setTimeout(poll, next);
    };

    // Expose poll so imperative callers (startTransfer) can refresh on demand.
    pollRef.current = poll;

    // Kick off immediately on mount.
    poll();

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        poll();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [handleActiveUpdate, ehrTransferEnabled]);

  const startTransfer = useCallback(
    async (audioId: number) => {
      if (!audioId) {
        message.error("Invalid note ID");
        return;
      }

      if (!credentialStatus?.has_credential) {
        window.open(CHROME_EXTENSION_URL, "_blank");
        return;
      }

      setIsLoading(true);

      try {
        const res = await startEHRTransfer(audioId);
        const data: StartEHRTransferResponse = res.data;

        if (data.success) {
          message.info("Transfer started");
          const status = data.status || "pending";
          setCurrentStatus(status);
          setJobId(data.job_id || null);
          // Optimistically flip this note's button (and the banner) without
          // waiting for the next poll. Upsert so other in-flight jobs survive.
          if (data.job_id) {
            const optimistic: TransferJob = {
              id: data.job_id,
              status,
              patient_name: "",
              requires_appointment_selection: false,
              audio_file_id: audioId,
            };
            setActiveJob(optimistic);
            setActiveJobs((prev) => {
              const without = prev.filter((j) => j.id !== optimistic.id);
              return [optimistic, ...without];
            });
          }
          eventEmitter.emit("ehrTransferStarted", { audioId });

          // The picker modal is opened by handleActiveUpdate from a poll
          // result, not from the optimistic activeJob above (which carries no
          // appointment_options). On the cached-appointments path the job is
          // already awaiting selection server-side, so force an immediate poll
          // instead of waiting up to IDLE_POLL_INTERVAL_MS for the next tick.
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          pollRef.current();
        } else {
          handleTransferError(data);
        }
      } catch (error) {
        console.error("Error starting EHR transfer:", error);
        const axiosError = error as { response?: { data?: StartEHRTransferResponse } };
        handleTransferError(axiosError.response?.data);
      } finally {
        setIsLoading(false);
      }
    },
    [credentialStatus],
  );

  const handleTransferError = (data?: StartEHRTransferResponse) => {
    const errorCode = data?.code;

    switch (errorCode) {
      case "no_credential":
        window.open(CHROME_EXTENSION_URL, "_blank");
        break;
      case "already_transferred":
        message.info("This note was already transferred");
        break;
      case "in_progress":
        message.info("Transfer is already in progress");
        setCurrentStatus(data?.status || "pending");
        setJobId(data?.job_id || null);
        break;
      case "no_client":
        message.error("Note has no associated client/patient");
        break;
      case "missing_patient_name":
      case "missing_patient_dob":
        message.error("Patient information is incomplete in client record");
        break;
      default:
        message.error(getUserFriendlyError(data?.error));
    }
  };

  const selectAppointment = useCallback(
    async (appointmentId: string) => {
      if (!jobId) {
        message.error("No active transfer job");
        return;
      }

      setIsSelectingAppointment(true);
      const wasNoteTypeSelection = currentStatusRef.current === "awaiting_note_type";

      try {
        const response = await selectTransferAppointment(jobId, appointmentId);
        setShowAppointmentModal(false);

        const responseStatus = response?.data?.status;
        if (responseStatus === "completed") {
          setCurrentStatus("completed");
          message.success("Note transferred to EHR successfully!");
        } else if (responseStatus === "failed") {
          setCurrentStatus("failed");
          message.error(getUserFriendlyError(response?.data?.error_message));
        } else {
          setCurrentStatus("template_mapping");
          message.info(
            wasNoteTypeSelection
              ? "Note template selected. Transfer in progress."
              : "Appointment selected. Transfer in progress.",
          );
        }
      } catch (error) {
        console.error("Error selecting appointment:", error);
        message.error(wasNoteTypeSelection ? "Failed to select note template" : "Failed to select appointment");
      } finally {
        setIsSelectingAppointment(false);
      }
    },
    [jobId],
  );

  const cancelAppointment = useCallback(async () => {
    setShowAppointmentModal(false);
    if (jobId) {
      setCurrentStatus("cancelled");
      try {
        await cancelEHRTransfer(jobId);
      } catch (error) {
        console.error("Failed to cancel transfer job:", error);
      }
    }
  }, [jobId]);

  const submitMFACode = useCallback(
    async (code: string, method?: string) => {
      if (!jobId) {
        message.error("No active transfer job");
        return;
      }

      setIsSubmittingMFA(true);

      try {
        await submitTransferMFACode(jobId, code || undefined, method);

        if (method && !code) {
          setShowMFAModal(false);
          setMfaOptions([]);
          message.info("Verification method selected. Waiting for code prompt...");
        } else {
          mfaSubmittedForJobRef.current = jobId;
          setShowMFAModal(false);
          setCurrentStatus("logging_in");
          message.info("Verification code submitted. Transfer resuming...");
        }
      } catch (error) {
        console.error("Error submitting MFA:", error);
        message.error("Failed to submit verification. Please try again.");
      } finally {
        setIsSubmittingMFA(false);
      }
    },
    [jobId],
  );

  const cancelMFA = useCallback(() => {
    setShowMFAModal(false);
  }, []);

  // Cancel a specific in-flight transfer (banner cancel button). Optimistically
  // marks it cancelled, closes the shared modals if they were this job's, then
  // re-syncs from the server.
  const cancelTransfer = useCallback(async (id: string) => {
    setActiveJobs((prev) => prev.map((j) => (j.id === id ? { ...j, status: "cancelled" } : j)));
    if (jobIdRef.current === id) {
      setShowAppointmentModal(false);
      setShowMFAModal(false);
      setCurrentStatus("cancelled");
    }
    try {
      await cancelEHRTransfer(id);
      message.info("Transfer cancelled");
    } catch (error) {
      console.error("Failed to cancel transfer job:", error);
      message.error("Failed to cancel transfer. Please try again.");
    } finally {
      pollRef.current();
    }
  }, []);

  // The most relevant live job for a note: prefer a non-terminal one, else the
  // most recent entry for that audio.
  const getJobForAudio = useCallback(
    (audioId: number): TransferJob | null => {
      const forAudio = activeJobs.filter((j) => j.audio_file_id === audioId);
      if (!forAudio.length) return null;
      return forAudio.find((j) => !TERMINAL_STATUSES.includes(j.status)) ?? forAudio[0];
    },
    [activeJobs],
  );

  const isNoteTypeSelection = currentStatus === "awaiting_note_type";

  const value: TransferContextValue = {
    isLoading,
    credentialStatus,
    activeJob,
    activeJobs,
    currentStatus,
    jobId,
    appointmentOptions,
    showAppointmentModal,
    isSelectingAppointment,
    isNoteTypeSelection,
    showMFAModal,
    isSubmittingMFA,
    mfaOptions,
    startTransfer,
    selectAppointment,
    cancelAppointment,
    submitMFACode,
    cancelMFA,
    cancelTransfer,
    getJobForAudio,
  };

  return <TransferContext.Provider value={value}>{children}</TransferContext.Provider>;
};
